import React, { useEffect } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import SectionTitle from '../sectionTitle';
import EzTextingLoader from '../ezTextingLoader';
import { RichText } from 'prismic-reactjs';

const DEFAULT_SCRIPT_URL =
  '//static.ctctcdn.com/js/signup-form-widget/current/signup-form-widget.min.js';
const DEFAULT_FORM_ID = '6b00a4f6-1783-4cd7-ab88-2005e3bb7437';

const looksLikeUrl = (str) => {
  if (!str || typeof str !== 'string') {
    return true;
  }
  const trimmed = str.trim();
  return (
    trimmed.startsWith('//') ||
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    /\.(js|min\.js)(\?|$)/i.test(trimmed)
  );
};

const ConstantContactSlice = (slice) => {
  const primary = slice?.slice?.primary ?? {};
  const scriptVariable = primary.constant_contact_script_variable ?? '';
  const scriptUrl = primary.constant_contact_script_url?.url || DEFAULT_SCRIPT_URL;
  const formId = primary.constant_contact_form_id || DEFAULT_FORM_ID;

  const rawTitle = primary.form_title
    ? (typeof primary.form_title === 'string'
        ? primary.form_title
        : RichText.asText(primary.form_title?.raw ?? []))
    : '';
  const formTitle =
    rawTitle && !looksLikeUrl(rawTitle) ? rawTitle : 'Sign up!';

  const rawDescription = primary.form_description
    ? (typeof primary.form_description === 'string'
        ? primary.form_description
        : RichText.asText(primary.form_description?.raw ?? []))
    : '';
  const formDescription =
    rawDescription && !looksLikeUrl(rawDescription)
      ? rawDescription
      : 'Sign up to be notified of upcoming sharpening events!';

  const siteData = useStaticQuery(graphql`
    query ConstantContactSiteQuery {
      site {
        siteMetadata {
          siteUrl
        }
      }
    }
  `);
  const siteUrl =
    siteData?.site?.siteMetadata?.siteUrl || 'https://www.noonsharpening.com';
  const normalizedOrigin = siteUrl
    .replace(/^http:\/\//i, 'https://')
    .replace(/\/$/, '');
  const recaptchaCoParam =
    typeof Buffer !== 'undefined'
      ? Buffer.from(normalizedOrigin, 'utf8').toString('base64')
      : typeof btoa !== 'undefined'
        ? btoa(unescape(encodeURIComponent(normalizedOrigin)))
        : 'aHR0cHM6Ly93d3cubm9vbnNoYXJwZW5pbmcuY29t';

  useEffect(() => {
    const scriptVar = document.createElement('script');
    scriptVar.innerText = `var _ctct_m = "${scriptVariable}";`;
    document.body.appendChild(scriptVar);

    const script = document.createElement('script');
    script.id = 'signupScript';
    script.src = scriptUrl;
    document.body.appendChild(script);

    return () => {
      if (script.parentNode === document.body) {
        document.body.removeChild(script);
      }
      if (scriptVar.parentNode === document.body) {
        document.body.removeChild(scriptVar);
      }
    };
  }, [scriptVariable, scriptUrl]);

  useEffect(() => {
    const removeDuplicateEmailListsField = () => {
      const ctctForm = document.querySelector('.ctct-inline-form form');
      if (!ctctForm) {
        return false;
      }
      const fields = ctctForm.querySelectorAll('[id*="field_"], .ctct-form-field');
      for (const field of fields) {
        const label = field.querySelector('label');
        const textInput = field.querySelector(
          'input[type="text"], input:not([type="email"]):not([type="checkbox"])'
        );
        const hasCheckboxes = field.querySelector('input[type="checkbox"]');
        if (label && textInput && !hasCheckboxes) {
          const labelText = (label.textContent || '')
            .trim()
            .replace(/\s*\*\s*$/, '')
            .toLowerCase();
          if (labelText === 'email lists') {
            field.remove();
            return true;
          }
        }
      }
      return false;
    };

    const attemptRemoval = (retries = 6, interval = 600) => {
      let attempt = 0;
      const id = setInterval(() => {
        if (removeDuplicateEmailListsField() || attempt >= retries) {
          clearInterval(id);
        }
        attempt++;
      }, interval);
      return () => clearInterval(id);
    };

    const cleanup = attemptRemoval();
    return cleanup;
  }, [formId]);

  return (
    <div className='mb-12 flex w-full flex-col items-center px-8 md:w-2/3 md:px-0'>
      <EzTextingLoader />
      <div>
        <form className="c-web-form" method="POST" action="https://widgy-lb.prd.cfire.io/EZ/widget/subscribe">
          <input type="hidden" name="serializedData" id="jwt" value="eyJlbmMiOiJBMTI4R0NNIiwiYnJhbmQiOiJFWiIsImFsZyI6ImRpciIsIndpZGdldElkIjoiYzc2MjMxNmYtYTY2OC00NjQzLTg4ODQtYmM4NjE0NDY5ZWJlIn0..sCNAVPKP7pp9OM3D.QMp0f0ZCG_xg69H1OzQWCLUnT4867ZXyeYY7kg3i5q6X5tiB0HlV9c02KZVS94HkJiefZq_XJYPeRd2N0n6DIKp57ePiqeL0EjBQ7J2r_BEjDIw1tlbRulFqfy9Wi9_ItDnJk569iJHg1mOt3TnuhrFq1MHw-su_4gifbctuk2h6X1tod3d1foD7dYGoC2VxJGe8yeFpyurBu3sU5aqrAlTR0Mc8cE29tNnbkjYPBt29qA.n6QcWGZdNqW1v6C3dwCAqQ" />
          <div className="c-web-form__title">Join our text list!</div>
          <div className="c-web-form__description">Sign up to be notified about sharpening!</div>
          <ul className="c-web-form__be-errors"></ul>
          <div>
            <div className="c-web-form__control-group">
              <label className="c-web-form__label c-web-form-uppercase-text" htmlFor="c-web-form-phoneNumber">Phone Number*</label>
              <div className="c-web-form__error" id="c-web-form-phoneNumber-error"></div>
              <input defaultValue="" className="c-web-form__input c-web-form--input-required" type="tel" name="phoneNumber" id="c-web-form-phoneNumber" maxLength="20" />
            </div>
          </div>
          <div className="c-web-form__control-group">
            <input className="c-web-form__checkbox" name="groupIds[]" type="checkbox" id="18783473003" value="18783473003" />
            <label className="c-web-form__label" htmlFor="18783473003">Chardon Heinen&#39;s</label>
          </div>
          <div className="c-web-form__control-group">
            <input className="c-web-form__checkbox" name="groupIds[]" type="checkbox" id="18783480003" value="18783480003" />
            <label className="c-web-form__label" htmlFor="18783480003">General Interest</label>
          </div>
          <div className="c-web-form__control-group">
            <input className="c-web-form__checkbox" name="groupIds[]" type="checkbox" id="18783454003" value="18783454003" />
            <label className="c-web-form__label" htmlFor="18783454003">Bay Village Heinen&#39;s</label>
          </div>
          <div className="c-web-form__control-group">
            <input className="c-web-form__checkbox" name="groupIds[]" type="checkbox" id="18783452003" value="18783452003" />
            <label className="c-web-form__label" htmlFor="18783452003">Pepper Pike Heinen&#39;s</label>
          </div>
          <div className="c-web-form__control-group">
            <input className="c-web-form__checkbox" name="groupIds[]" type="checkbox" id="18783477003" value="18783477003" />
            <label className="c-web-form__label" htmlFor="18783477003">Island Hardware</label>
          </div>
          <div className="c-web-form__control-group">
            <input className="c-web-form__checkbox" name="groupIds[]" type="checkbox" id="18783469003" value="18783469003" />
            <label className="c-web-form__label" htmlFor="18783469003">Local Roots Market</label>
          </div>
          <div className="c-web-form__control-group">
            <input className="c-web-form__checkbox" name="groupIds[]" type="checkbox" id="18783475003" value="18783475003" />
            <label className="c-web-form__label" htmlFor="18783475003">Glenview, IL Heinen&#39;s</label>
          </div>
          <div className="c-web-form__control-group">
            <input className="c-web-form__checkbox" name="groupIds[]" type="checkbox" id="18783460003" value="18783460003" />
            <label className="c-web-form__label" htmlFor="18783460003">Lake Bluff, IL Heinen&#39;s</label>
          </div>
          <div className="c-web-form__control-group">
            <input className="c-web-form__checkbox" name="groupIds[]" type="checkbox" id="18783449003" value="18783449003" />
            <label className="c-web-form__label" htmlFor="18783449003">Chagrin Falls Heinen&#39;s</label>
          </div>
          <div className="c-web-form__control-group">
            <input className="c-web-form__checkbox" name="groupIds[]" type="checkbox" id="21943596003" value="21943596003" />
            <label className="c-web-form__label" htmlFor="21943596003">Purplebrown Farm Store</label>
          </div>
          <div className="c-web-form__control-group">
            <input className="c-web-form__checkbox" name="groupIds[]" type="checkbox" id="44739666003" value="44739666003" />
            <label className="c-web-form__label" htmlFor="44739666003">Naperville Heinen&#39;s</label>
          </div>
          <div className="c-web-form__control-group">
            <input className="c-web-form__checkbox" name="groupIds[]" type="checkbox" id="18783446003" value="18783446003" />
            <label className="c-web-form__label" htmlFor="18783446003">Strongsville Heinen&#39;s</label>
          </div>
          <div className="c-web-form__control-group">
            <input className="c-web-form__checkbox" name="groupIds[]" type="checkbox" id="18783445003" value="18783445003" />
            <label className="c-web-form__label" htmlFor="18783445003">Rocky River Heinen&#39;s</label>
          </div>
          <div className="c-web-form__control-group">
            <input className="c-web-form__checkbox" name="groupIds[]" type="checkbox" id="31121285003" value="31121285003" />
            <label className="c-web-form__label" htmlFor="31121285003">Mayfield Heinen&#39;s</label>
          </div>
          <div className="c-web-form__control-group">
            <input className="c-web-form__checkbox" name="groupIds[]" type="checkbox" id="18783455003" value="18783455003" />
            <label className="c-web-form__label" htmlFor="18783455003">Avon Heinen&#39;s</label>
          </div>
          <div className="c-web-form__control-group">
            <input className="c-web-form__checkbox" name="groupIds[]" type="checkbox" id="18783447003" value="18783447003" />
            <label className="c-web-form__label" htmlFor="18783447003">Brecksville Heinen&#39;s</label>
          </div>
          <div className="c-web-form__control-group">
            <input className="c-web-form__checkbox" name="groupIds[]" type="checkbox" id="18783464003" value="18783464003" />
            <label className="c-web-form__label" htmlFor="18783464003">Hemly Hardware</label>
          </div>
          <div className="c-web-form__control-group">
            <input className="c-web-form__checkbox" name="groupIds[]" type="checkbox" id="18783451003" value="18783451003" />
            <label className="c-web-form__label" htmlFor="18783451003">Barrington, IL Heinen&#39;s</label>
          </div>
          <div className="c-web-form__control-group">
            <input className="c-web-form__checkbox" name="groupIds[]" type="checkbox" id="18783462003" value="18783462003" />
            <label className="c-web-form__label" htmlFor="18783462003">Bannockburn, IL Heinen&#39;s</label>
          </div>
          <div className="c-web-form__control-group">
            <input className="c-web-form__checkbox" name="groupIds[]" type="checkbox" id="18783459003" value="18783459003" />
            <label className="c-web-form__label" htmlFor="18783459003">University Hts. Heinen&#39;s</label>
          </div>
          <div className="c-web-form__control-group">
            <input className="c-web-form__checkbox" name="groupIds[]" type="checkbox" id="31121474003" value="31121474003" />
            <label className="c-web-form__label" htmlFor="31121474003">Shaker Heights Heinen&#39;s</label>
          </div>
          <div className="c-web-form__control-group">
            <input className="c-web-form__checkbox" name="groupIds[]" type="checkbox" id="18783458003" value="18783458003" />
            <label className="c-web-form__label" htmlFor="18783458003">Aurora Heinen&#39;s</label>
          </div>
          <div className="c-web-form__control-group">
            <input className="c-web-form__checkbox" name="groupIds[]" type="checkbox" id="18783457003" value="18783457003" />
            <label className="c-web-form__label" htmlFor="18783457003">Bainbridge Heinen&#39;s</label>
          </div>
          <div className="c-web-form__control-group">
            <input className="c-web-form__checkbox" name="groupIds[]" type="checkbox" id="18783467003" value="18783467003" />
            <label className="c-web-form__label" htmlFor="18783467003">Twinsburg Heinen&#39;s</label>
          </div>
          <div className="c-web-form__control-group">
            <input className="c-web-form__checkbox" type="checkbox" id="c-web-form-agree" onChange={(e) => {
              const submitBtn = document.querySelector('.c-web-form__submit');
              if (submitBtn) {
                if (e.target.checked) {
                  submitBtn.removeAttribute('disabled');
                } else {
                  submitBtn.setAttribute('disabled', 'disabled');
                }
              }
            }} />
            <label className="c-web-form__label" htmlFor="c-web-form-agree">
              By checking this box, you agree to receive text messages from Noon Sharpening, Inc..
              Recurring messages subscription. Message and data rates may apply.
              Reply HELP for help and STOP to opt out.
            </label>
          </div>
          <div className="c-web-form__control-group">
            <button type="submit" className="c-web-form__submit" disabled>Sign Up</button>
          </div>
          <p className="c-success-message">Thank you!</p>
          <div className="c-web-form__footer">
            SMS sharing disclosure: No mobile information will be shared with third parties/affiliates for marketing/promotional purposes at anytime.
            <br />
            <i>Powered by <a className="c-web-form__link c-web-form-semibold-text" href="https://eztexting.com">EZ Texting</a></i>
          </div>
        </form>
      </div>
      <SectionTitle title={slice.slice.primary.title.raw} />
      <div
        className='ctct-inline-form w-full md:w-2/3'
        data-form-id={formId}
      >
        <div className='ctct-form-container ctct-form-embed form_0'>
          <div className='ctct-form-defaults' data-qe-id='form-background'>
            <div
              id='success_message_0'
              className='ctct-form-success'
              style={{ display: `none` }}
              data-qe-id='success-message'
            >
              <h2 className='ctct-form-header'>Thanks for signing up!</h2>
              <p className='ctct-form-text'>
                You can unsubscribe at any time using the Unsubscribe link at
                the bottom of every email.
              </p>
            </div>
            <form
              className='ctct-form-custom'
              id='ctct_form_0'
              autoComplete='on'
              data-qe-id='form-data'
            >
              <h2
                data-qe-id='form-title'
                className='ctct-form-header py-4 text-2xl'
              >
                {formTitle}
              </h2>
              <p data-qe-id='form-description' className='ctct-form-text py-8'>
                {formDescription}
              </p>
              <div id='email_address_field_0' className='ctct-form-field'>
                <label
                  data-qe-id='form-label-email'
                  id='email_address_label_0'
                  htmlFor='email_address_0'
                  className='ctct-form-label ctct-form-required'
                >
                  Email
                </label>
                <div
                  className='ctct-form-errorMessage'
                  data-qe-id='form-error-email'
                  style={{ display: `none` }}
                ></div>
                <input
                  className='ctct-form-element'
                  data-qe-id='form-input-email'
                  id='email_address_0'
                  type='email'
                  name='email_address'
                  defaultValue=''
                  maxLength='80'
                />
              </div>
              <div
                id='error_message_0'
                className='ctct-form-error'
                style={{ display: `none` }}
              >
                <p className='ctct-form-errorMessage'>
                  Sorry, we could not complete your sign-up. Please contact us
                  to resolve this.
                </p>
              </div>
              <div
                id='network_error_message_0'
                className='ctct-form-error'
                style={{ display: `none` }}
              >
                <p className='ctct-form-errorMessage'>
                  Operation timed out, please try again.
                </p>
              </div>
              <div id='gdpr_text'>
                <p
                  className='ctct-gdpr-text py-4 text-sm'
                  data-qe-id='form-gdpr-text'
                >
                  By submitting this form, you are consenting to receive
                  marketing emails from: Noon Sharpening, Inc., 52 Vincent
                  Avenue, Northfield, OH, 44067, US,
                  http://www.noonsharpening.com. You can revoke your consent to
                  receive emails at any time by using the SafeUnsubscribe® link,
                  found at the bottom of every email.
                  <a
                    href='https://www.constantcontact.com/legal/service-provider'
                    target='_blank'
                    rel='noreferrer noopener'
                    data-qe-id='form-service-link'
                    className='ctct-form-footer-link'
                  >
                    Emails are serviced by Constant Contact.
                  </a>
                </p>
              </div>
              <button
                data-qe-id='form-button'
                type='submit'
                className='ctct-form-button my-4'
              >
                Sign Up!
              </button>
              <div
                className='g-recaptcha'
                id='ctct_recaptcha_0'
                data-sitekey='6LfHrSkUAAAAAPnKk5cT6JuKlKPzbwyTYuO8--Vr'
                data-size='invisible'
              >
                <div
                  className='grecaptcha-badge'
                  data-style='bottomright'
                  style={{
                    width: `256px`,
                    height: `60px`,
                    display: `block`,
                    transition: `right 0.3s ease 0s`,
                    position: `fixed`,
                    bottom: `14px`,
                    right: `-186px`,
                    boxShadow: `gray 0px 0px 5px`,
                    borderRadius: `2px`,
                    overflow: `hidden`,
                  }}
                >
                  <div className='grecaptcha-logo'>
                    <iframe
                      title='reCAPTCHA'
                      src={`https://www.google.com/recaptcha/api2/anchor?ar=1&k=6LfHrSkUAAAAAPnKk5cT6JuKlKPzbwyTYuO8--Vr&co=${recaptchaCoParam}&hl=en&v=qljbK_DTcvY1PzbR7IG69z1r&size=invisible&cb=89fquz4kr9i4`}
                      role='presentation'
                      name='a-h9x7g46wzjn7'
                      scrolling='no'
                      sandbox='allow-forms allow-popups allow-same-origin allow-scripts allow-top-navigation allow-modals allow-popups-to-escape-sandbox allow-storage-access-by-user-activation'
                      width='256'
                      height='60'
                      frameBorder='0'
                    ></iframe>
                  </div>
                  <div className='grecaptcha-error'></div>
                  <textarea
                    id='g-recaptcha-response'
                    name='g-recaptcha-response'
                    className='g-recaptcha-response'
                    style={{
                      width: `250px`,
                      height: `40px`,
                      border: `1px solid rgb(193, 193, 193)`,
                      margin: `10px 25px`,
                      padding: `0px`,
                      resize: `none`,
                      display: `none`,
                    }}
                  ></textarea>
                </div>
                <iframe style={{ display: `none` }}></iframe>
              </div>
            </form>
            <div className='ctct-form-footer my-4 flex w-full items-center justify-center'>
              <a
                href='http://www.constantcontact.com/index.jsp?cc=forms_popup'
                target='_blank'
                rel='nofollow noopener noreferrer'
                className='ctct-form-footer-link'
              >
                <img
                  src='https://static.ctctcdn.com/lp/images/standard/logos/logo-ctct-white.svg?v=2020'
                  alt=''
                  className='ctct-form-footer-img text-center'
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConstantContactSlice;
